import type { EnvironmentId, ProjectId } from "@afar-research/contracts";
import { ArrowLeftIcon } from "lucide-react";
import { useEffect, useMemo } from "react";

import {
  pullRequestSurface,
  usePullRequestSurfaceStore,
  type PullRequestSurfaceDescriptor,
} from "../../pullRequestSurfaceStore";
import { PullRequestDetailSurface } from "../PullRequestSurfacePanel";
import { Button } from "../ui/button";

/**
 * One pull request beside a thread's conversation. The thread and the Pull Requests workspace
 * keep separate selections: this panel's placement lives in the thread's own right panel, while
 * the mutation sessions are the shared ones a pull request has wherever it is opened, so an
 * unsent review does not depend on which surface happened to render it.
 */
export function ThreadPullRequestDetail({
  environmentId,
  projectId,
  host,
  repository,
  number,
  url,
  onBack,
}: {
  readonly environmentId: EnvironmentId;
  readonly projectId: ProjectId;
  readonly host?: string | undefined;
  readonly repository: string;
  readonly number: number;
  readonly url: string;
  readonly onBack: () => void;
}) {
  const ensureSessions = usePullRequestSurfaceStore((state) => state.ensureSessions);
  const descriptor: PullRequestSurfaceDescriptor = useMemo(
    () =>
      pullRequestSurface({
        environmentId,
        projectId,
        ...(host === undefined ? {} : { host }),
        repository,
        number,
        url,
      }),
    [environmentId, host, number, projectId, repository, url],
  );
  useEffect(() => {
    ensureSessions(descriptor);
  }, [descriptor, ensureSessions]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-center gap-2 border-b border-border px-2 py-1.5">
        <Button size="xs" variant="ghost" onClick={onBack}>
          <ArrowLeftIcon className="size-3.5" />
          Linked pull requests
        </Button>
        <span className="truncate font-mono text-[11px] text-muted-foreground">
          {repository}#{number}
        </span>
      </div>
      <PullRequestDetailSurface key={descriptor.id} descriptor={descriptor} />
    </div>
  );
}
